/**
 * Why this payment moved.
 *
 * A deferral is a decision taken on someone's behalf about when she gets her
 * money, so "the system moved it" is not an answer. payment_deferrals records
 * which period was full, what the ceiling was, how much was already counted
 * against it and what the overshoot would have been; this renders exactly that,
 * with the regulation that set the ceiling named.
 *
 * Two audiences, one component:
 *   tone="staff"  — the full arithmetic, for a counsellor who has to answer for it.
 *   tone="member" — the date and the amount, with no talk of ceilings or
 *                   breaches. She does not need to know the programme nearly
 *                   broke a SNAP rule; she needs to know her $125 arrives on
 *                   1 January and nothing is wrong.
 */

import React from 'react';
import { CalendarClock } from 'lucide-react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { formatDateOnly } from '../../utils/dateTime';

export interface PayoutDeferral {
  period_start: string;
  period_end: string;
  period_cap: number;
  period_total_before: number;
  amount: number;
  would_have_been: number;
  deferred_to: string;
  reason: string;
  released_at?: string | null;
  cap_basis?: string | null;
}

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

interface Props {
  deferral: PayoutDeferral;
  tone?: 'staff' | 'member';
}

export const PayoutDeferralNotice: React.FC<Props> = ({ deferral, tone = 'staff' }) => {
  const { fmt } = useFormattedDate();
  // These are DATE columns, not timestamps. Running them through the timezone
  // formatter shifts them a day in every US zone, and the federal fiscal
  // quarter boundary is 30 Sep / 1 Oct — a one-day shift names the wrong
  // quarter, which is the whole thing this screen exists to get right.
  const arrives = formatDateOnly(deferral.deferred_to);
  const overshoot = Math.max(0, (deferral.would_have_been ?? 0) - (deferral.period_cap ?? 0));

  if (tone === 'member') {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3">
        <CalendarClock className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" aria-hidden="true" />
        <div className="text-sm text-sky-900">
          <p className="font-medium">
            {money(deferral.amount)} is scheduled for {arrives}.
          </p>
          <p className="mt-1 text-sky-800">
            We spread payments across the year so they do not affect the benefits your household
            already receives. Nothing is wrong, and you are not losing this payment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3">
      <div className="flex items-start gap-2">
        <CalendarClock className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm text-sky-900">
          <p className="font-medium">
            Moved to {arrives} to stay under a benefit ceiling.
          </p>

          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs tabular-nums sm:grid-cols-4">
            <div>
              <dt className="text-sky-700">Period</dt>
              <dd className="font-medium">
                {formatDateOnly(deferral.period_start)} – {formatDateOnly(deferral.period_end)}
              </dd>
            </div>
            <div>
              <dt className="text-sky-700">Already counted</dt>
              <dd className="font-medium">{money(deferral.period_total_before)}</dd>
            </div>
            <div>
              <dt className="text-sky-700">This payment</dt>
              <dd className="font-medium">{money(deferral.amount)}</dd>
            </div>
            <div>
              <dt className="text-sky-700">Ceiling</dt>
              <dd className="font-medium">{money(deferral.period_cap)}</dd>
            </div>
          </dl>

          {overshoot > 0 && (
            <p className="mt-2 text-xs text-sky-800">
              Paying it in that period would have reached {money(deferral.would_have_been)} —{' '}
              <strong>{money(overshoot)} over</strong>. She does not lose the money; it moves.
            </p>
          )}

          {deferral.cap_basis && (
            <p className="mt-2 border-t border-sky-200 pt-2 text-xs text-sky-700">
              {deferral.cap_basis}
            </p>
          )}

          {deferral.released_at && (
            <p className="mt-2 text-xs text-sky-700">
              Released {fmt.dateTime(deferral.released_at)}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutDeferralNotice;
